import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import {useScroll} from 'motion/react'
import {getTopBannerDismissed, setTopBannerDismissed} from 'utils/persistence'

import {isBodyScrollLocked} from '@vio/atlas/Overlay/useBodyScrollLock'

export const BANNER_HEIGHT = 64

interface TopBannerContextType {
  isVisible: boolean
  isScrollCollapsed: boolean
  bannerHeight: number // Dynamic height that changes with scroll (64 -> 0 -> 64)
  dismiss: () => void
}

const TopBannerContext = createContext<TopBannerContextType | null>(null)

/**
 * Provider component for top banner state
 */
export const TopBannerProvider = ({children}: {children: React.ReactNode}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isScrollCollapsed, setIsScrollCollapsed] = useState(false)
  const {scrollY} = useScroll()

  // Dynamic height that changes with scroll and visibility
  const bannerHeight = isVisible && !isScrollCollapsed ? BANNER_HEIGHT : 0

  useEffect(() => {
    const isDismissed = getTopBannerDismissed()
    setIsVisible(!isDismissed)
  }, [])

  // Track scroll direction and position for collapsing banner
  useEffect(() => {
    if (!isVisible) {
      setIsScrollCollapsed(false)
      return
    }

    let lastScrollY = 0
    const threshold = 65 // Pixels to scroll before hiding banner matches with top nav on SRP
    const topThreshold = 10 // Pixels from top to show banner again

    const updateScrollDirection = (latest: number) => {
      if (isBodyScrollLocked()) return
      if (Math.abs(latest - lastScrollY) < 10) return // Ignore small movements

      if (latest > threshold && latest > lastScrollY) {
        // Scrolling down and past threshold - hide banner
        setIsScrollCollapsed(true)
      } else if (latest <= topThreshold) {
        // Scrolled all the way to the top - show banner
        setIsScrollCollapsed(false)
      }
      // Note: We don't show banner when scrolling up from middle of page

      lastScrollY = latest
    }

    const unsubscribe = scrollY.on('change', updateScrollDirection)
    return () => unsubscribe()
  }, [scrollY, isVisible])

  const dismiss = useCallback(() => {
    setTopBannerDismissed()
    setIsVisible(false)
  }, [])

  return (
    <TopBannerContext.Provider
      value={{isVisible, isScrollCollapsed, bannerHeight, dismiss}}
    >
      {children}
    </TopBannerContext.Provider>
  )
}

/**
 * Hook for managing top banner visibility using cookies and AB test
 *
 * Uses cookies instead of localStorage for consistency with other components like popupOnExit.
 * The dismissed state expires after 30 days automatically via cookie expiration.
 *
 * @returns Object containing isVisible, isScrollCollapsed, bannerHeight, and dismiss function
 */
export const useTopBanner = () => {
  const context = useContext(TopBannerContext)

  if (!context) {
    throw new Error('useTopBanner must be used within a TopBannerProvider')
  }

  return context
}
